import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Pagination,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { monitoringService, LogFile, LogSearchResult } from '@/services/monitoringService';
import LogViewer from './LogViewer';

interface LogSearchCardProps {
  logFiles: LogFile[];
  availableLevels: string[];
  formatFileSize: (bytes: number) => string;
}

const LogSearchCard: React.FC<LogSearchCardProps> = ({
  logFiles,
  availableLevels,
  formatFileSize,
}) => {
  const [searchFile, setSearchFile] = useState<string>(logFiles[0]?.name || '');
  const [searchPattern, setSearchPattern] = useState<string>('');
  const [searchLevel, setSearchLevel] = useState<string>('');
  const [searchResults, setSearchResults] = useState<LogSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchPage, setSearchPage] = useState(1);

  // Update default file when logFiles changes
  React.useEffect(() => {
    if (logFiles.length > 0 && !searchFile) {
      setSearchFile(logFiles[0].name);
    }
  }, [logFiles, searchFile]);

  const handleSearch = async (page = 1) => {
    if (!searchFile) {
      toast.error('Please select a log file');
      return;
    }

    setSearching(true);
    setSearchPage(page);
    try {
      const results = await monitoringService.searchLogs({
        file: searchFile,
        pattern: searchPattern || undefined,
        level: searchLevel || undefined,
        page,
        pageSize: 50,
      });
      setSearchResults(results);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Log Search
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Log File</InputLabel>
            <Select
              value={searchFile}
              onChange={(e) => setSearchFile(e.target.value)}
              label="Log File"
            >
              {logFiles.map((file) => (
                <MenuItem key={file.name} value={file.name}>
                  {file.name} ({formatFileSize(file.size)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Search Pattern"
            size="small"
            value={searchPattern}
            onChange={(e) => setSearchPattern(e.target.value)}
            placeholder="Regex pattern..."
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Level</InputLabel>
            <Select
              value={searchLevel}
              onChange={(e) => setSearchLevel(e.target.value)}
              label="Level"
            >
              <MenuItem value="">All</MenuItem>
              {availableLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => handleSearch(1)}
            disabled={searching || !searchFile}
            startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
          >
            Search
          </Button>
        </Box>

        {searchResults && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Found {searchResults.total} results
            </Typography>
            <LogViewer logs={searchResults.entries} autoScroll={false} maxHeight={300} />
            {searchResults.total > searchResults.pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(searchResults.total / searchResults.pageSize)}
                  page={searchPage}
                  onChange={(_, page) => handleSearch(page)}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LogSearchCard;
